import { memo, useEffect, useRef } from "react";
import { CustomerNotifiableEvents } from "../../../../types/prodcatBaseTypes";
import { noticeError } from "../../../../utils/newrelic/requests";

const BASKET_CLIENT_ID = "web_pdp_scaffold_ui";

type Props = {
  pimProductId: string;
  skuId: string;
  variantId: string;
  title: string;
  price: string;
  availableToOrder: boolean;
  customerNotifiableEvents: CustomerNotifiableEvents;
  hideAddToBasket: boolean;
  sendVariantIdToBasket: boolean;
  recsPlacementId: string;
  shouldEnableAddToBasket: boolean;
};

export function AddToBasketEsiWrapper({
  pimProductId,
  skuId,
  variantId,
  title,
  price,
  availableToOrder,
  customerNotifiableEvents,
  hideAddToBasket,
  sendVariantIdToBasket,
  recsPlacementId,
  shouldEnableAddToBasket,
}: Props) {
  const fixedSrc = useRef(
    getAddToBasketEsiSrcUrl(
      pimProductId,
      sendVariantIdToBasket,
      {
        skuId,
        variantId,
        title,
        price,
        availableToOrder,
        customerNotifiableEvents,
      },
      shouldEnableAddToBasket,
      hideAddToBasket,
      recsPlacementId,
    ),
  );

  return <MemoedEsi srcUrl={fixedSrc.current} />;
}

const MemoedEsi = memo(({ srcUrl }: { srcUrl: string }) => {
  const isMounting = useRef(true);

  useEffect(() => {
    if (!isMounting.current) {
      noticeError("Add to Basket component re-rendered unexpectedly!");
    }
    isMounting.current = false;
  });

  return (
    <div
      id="product:basket:a2besi"
      data-testid="product:basket:a2besi"
      className="esi-wrapper"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        // nosemgrep
        __html: `<esi:include id="web-pdp-scaffold-ui-add-to-basket" ${
          // nosemgrep
          srcUrl ? ` src="${srcUrl}"` : ""
        } />`,
      }}
    ></div>
  );
});
MemoedEsi.displayName = "MemoedEsi";

function getAddToBasketEsiSrcUrl(
  pimProductId: string,
  sendVariantId: boolean,
  variant: {
    skuId: string;
    variantId: string;
    title: string;
    price: string;
    availableToOrder: boolean;
    customerNotifiableEvents: CustomerNotifiableEvents;
  },
  shouldEnableAddToBasket: boolean,
  hideAddToBasket: boolean,
  recsPlacementId: string,
) {
  const queryString = makeQueryString({
    skuId: variant.skuId,
    variantId: sendVariantId ? variant.variantId : "",
    title: variant.title,
    price: variant.price,
    enabled: String(shouldEnableAddToBasket),
    includeCoreAssets: "false",
    hidden: String(hideAddToBasket),
    clientId: BASKET_CLIENT_ID,
    recsPlacementId,
  });

  return `/esi/add-to-basket/${pimProductId}?${queryString}`;
}

function makeQueryString(params: Record<string, string>) {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
}
